/**
 * Broadcast Store
 * 브로드캐스트 상태 관리
 */

import { create } from 'zustand';
import { BroadcastClient } from '../services/BroadcastClient';
import { BroadcastMessage } from '../services/protocol';

export interface BroadcastStats {
  messagesSent: number;
  messagesReceived: number;
  subscribers: number;
  lastMessageTime: number;
  averageResponseTime: number;
  errorCount: number;
}

export interface ReceivedMessage {
  id: string;
  timestamp: number;
  deviceId: string;
  payload: any;
  types: string[]; // ['eeg-raw', 'ppg-analysis' 등]
}

interface BroadcastStore {
  // 연결 상태
  isConnected: boolean;
  isSubscribing: boolean;
  serverUrl: string | null;
  deviceId: string | null;
  client: BroadcastClient | null;
  
  // 통계
  stats: BroadcastStats;
  
  // 수신된 메시지
  receivedMessages: ReceivedMessage[];
  maxMessages: number;
  
  // 에러
  lastError: string | null;
  
  // 액션
  connect: (serverUrl: string, deviceId: string) => Promise<void>;
  disconnect: () => void;
  publish: (payload: BroadcastMessage['payload']) => Promise<PublishResult>;
  startSubscription: () => void;
  stopSubscription: () => void;
  checkHealth: () => Promise<any>;
  getServerStats: () => Promise<any>;
  clearMessages: () => void;
  clearError: () => void;
  updateDeviceId: (deviceId: string) => void;
}

interface PublishResult {
  ok: boolean;
  subscribers: number;
  responseTime: number;
}

export const useBroadcastStore = create<BroadcastStore>((set, get) => ({
  // 초기 상태
  isConnected: false,
  isSubscribing: false,
  serverUrl: null,
  deviceId: null,
  client: null,
  
  stats: {
    messagesSent: 0,
    messagesReceived: 0,
    subscribers: 0,
    lastMessageTime: 0,
    averageResponseTime: 0,
    errorCount: 0
  },
  
  receivedMessages: [],
  maxMessages: 50,
  lastError: null,
  
  /**
   * 서버 연결
   */
  connect: async (serverUrl: string, deviceId: string) => {
    try {
      const client = new BroadcastClient({
        serverUrl,
        deviceId,
        autoReconnect: true
      });
      
      // Health check로 연결 확인
      await client.checkHealth();
      
      set({
        client,
        serverUrl,
        deviceId,
        isConnected: true,
        lastError: null
      });
      
      console.log('✅ Connected to broadcast server');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection failed';
      set({
        lastError: errorMessage,
        isConnected: false
      });
      throw error;
    }
  },
  
  /**
   * 서버 연결 해제
   */
  disconnect: () => {
    const { client } = get();
    if (client) {
      client.unsubscribe();
    }
    
    set({
      isConnected: false,
      isSubscribing: false,
      client: null,
      serverUrl: null,
      lastError: null
    });
    
    console.log('🔌 Disconnected from broadcast server');
  },
  
  /**
   * 데이터 발행
   */
  publish: async (payload: BroadcastMessage['payload']) => {
    const { client, stats } = get();
    
    if (!client) {
      throw new Error('Not connected to server');
    }
    
    const startTime = Date.now();
    
    try {
      const result = await client.publish(payload);
      const responseTime = Date.now() - startTime;
      
      // 통계 업데이트
      const newStats = {
        ...stats,
        messagesSent: stats.messagesSent + 1,
        subscribers: result.subscribers,
        lastMessageTime: Date.now(),
        averageResponseTime: stats.averageResponseTime === 0 
          ? responseTime 
          : (stats.averageResponseTime * 0.9 + responseTime * 0.1) // 이동 평균
      };
      
      set({ stats: newStats });
      
      return {
        ok: result.ok,
        subscribers: result.subscribers,
        responseTime
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Publish failed';
      set((state) => ({
        lastError: errorMessage,
        stats: {
          ...state.stats,
          errorCount: state.stats.errorCount + 1
        }
      }));
      throw error;
    }
  },
  
  /**
   * 구독 시작
   */
  startSubscription: () => {
    const { client, deviceId } = get();
    
    if (!client || !deviceId) {
      throw new Error('Not connected to server');
    }
    
    client.subscribe(
      (data: BroadcastMessage) => {
        console.log('[Store] onData called, deviceId:', data.deviceId, 'payload keys:', Object.keys(data.payload || {}));
        const state = get();
        
        // 메시지 타입 파악
        const types: string[] = [];
        if (data.payload.eegRaw) types.push('eeg-raw');
        if (data.payload.ppgRaw) types.push('ppg-raw');
        if (data.payload.accRaw) types.push('acc-raw');
        if (data.payload.eegAnalysis) types.push('eeg-analysis');
        if (data.payload.ppgAnalysis) types.push('ppg-analysis');
        if (data.payload.accAnalysis) types.push('acc-analysis');
        
        // 수신된 메시지 추가
        const newMessage: ReceivedMessage = {
          id: `${data.deviceId}-${data.timestamp}`,
          timestamp: data.timestamp,
          deviceId: data.deviceId,
          payload: data.payload,
          types
        };
        
        const newMessages = [...state.receivedMessages, newMessage];
        
        // 최대 메시지 수 유지
        if (newMessages.length > state.maxMessages) {
          newMessages.shift();
        }
        
        // 통계 업데이트
        const newStats = {
          ...state.stats,
          messagesReceived: state.stats.messagesReceived + 1,
          lastMessageTime: Date.now()
        };
        
        set({
          receivedMessages: newMessages,
          stats: newStats
        });
        console.log('[Store] ✅ message added. Total received:', newMessages.length);
      },
      (error) => {
        console.error('SSE error:', error);
        set({ lastError: 'SSE connection error' });
      }
    );
    
    set({ isSubscribing: true });
    console.log('🔔 Subscription started');
  },
  
  /**
   * 구독 중지
   */
  stopSubscription: () => {
    const { client } = get();
    if (client) {
      client.unsubscribe();
    }
    set({ isSubscribing: false });
    console.log('🔕 Subscription stopped');
  },
  
  /**
   * Health check
   */
  checkHealth: async () => {
    const { client } = get();
    if (!client) {
      throw new Error('Not connected to server');
    }
    
    try {
      const health = await client.checkHealth();
      return health;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Health check failed';
      set({ lastError: errorMessage });
      throw error;
    }
  },
  
  /**
   * 서버 통계 조회
   */
  getServerStats: async () => {
    const { client } = get();
    if (!client) {
      throw new Error('Not connected to server');
    }
    
    try {
      const stats = await client.getStats();
      return stats;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get stats';
      set({ lastError: errorMessage });
      throw error;
    }
  },
  
  /**
   * 메시지 로그 초기화
   */
  clearMessages: () => {
    set({ receivedMessages: [] });
  },
  
  /**
   * 에러 초기화
   */
  clearError: () => {
    set({ lastError: null });
  },
  
  /**
   * Device ID 업데이트
   */
  updateDeviceId: (deviceId: string) => {
    const { client } = get();
    if (client) {
      client.setDeviceId(deviceId);
    }
    set({ deviceId });
  }
}));

